import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Building2, Trash2, Users, Ban, CheckCircle2, UserPlus, Settings2, Copy, Mail, History } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Page";

const EMPTY_FORM = { name: "", company_address: "", email_reply_to: "", admin_name: "", admin_username: "", admin_password: "", admin_email: "", admin_phone: "" };

export default function Firmen() {
  const navigate = useNavigate();
  const { enterImpersonation } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [created, setCreated] = useState(null);
  const [userFor, setUserFor] = useState(null);
  const [userForm, setUserForm] = useState({ name: "", username: "", password: "", employee_type: "festangestellt", notify_email: "" });
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const load = async () => setRows((await api.get("/super/companies")).data || []);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Firmenname erforderlich");
    if ((form.admin_username && !form.admin_password) || (!form.admin_username && form.admin_password)) {
      return toast.error("Für den Admin bitte Benutzername UND Passwort angeben");
    }
    try {
      const r = await api.post("/super/companies", form);
      setOpen(false);
      if (r.data?.admin_created) {
        setCreated({ company: r.data.name, username: r.data.admin_created.username, password: form.admin_password, email: form.admin_email || "" });
      } else {
        toast.success("Firma angelegt");
      }
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const manage = async (c) => {
    try {
      const r = await api.post(`/super/companies/${c.id}/impersonate`);
      await enterImpersonation(r.data.token);
      navigate("/app/dashboard");
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.post(`/super/companies/${id}/status`, null, { params: { status } }); toast.success(status === "suspended" ? "Firma gesperrt" : "Firma aktiviert"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleModule = async (id, patch) => {
    try { await api.post(`/super/companies/${id}/modules`, patch); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    try { await api.delete(`/super/companies/${id}`); toast.success("Firma gelöscht"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const createUser = async () => {
    if (!userForm.username || !userForm.password || !userForm.name) return toast.error("Bitte alle Felder ausfüllen");
    try {
      await api.post(`/platform/companies/${userFor.id}/users`, userForm);
      toast.success("Mitarbeiter angelegt");
      setUserFor(null);
      setUserForm({ name: "", username: "", password: "", employee_type: "festangestellt", notify_email: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const portalUrl = `${window.location.origin}/login`;
  const copyCreds = () => {
    if (!created) return;
    const txt = `Teamtempus – Zugang für ${created.company}\nPortal: ${portalUrl}\nBenutzername: ${created.username}\nPasswort: ${created.password}`;
    navigator.clipboard?.writeText(txt).then(() => toast.success("Zugangsdaten kopiert")).catch(() => {});
  };

  const sendCreds = async () => {
    if (!created?.email) return toast.error("Bitte E-Mail-Adresse angeben");
    try {
      await api.post("/super/send-credentials", { to_email: created.email, company_name: created.company, username: created.username, password: created.password, portal_url: window.location.origin });
      toast.success("Zugang per E-Mail gesendet");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAudit = async (c) => {
    setAuditFor(c);
    setAuditRows([]);
    try { setAuditRows((await api.get(`/super/companies/${c.id}/audit`)).data || []); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Firmenverwaltung"
        subtitle="Firmen anlegen, verwalten, sperren und Module freischalten."
        testid="firmen-header"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="company-open" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-2" /> Neue Firma</Button></DialogTrigger>
            <DialogContent data-testid="company-dialog" className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Neue Firma anlegen</DialogTitle><DialogDescription>Firma anlegen und optional direkt den Admin-Zugang erstellen.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Firmenname</Label><Input data-testid="company-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Firmenadresse</Label><Input data-testid="company-address" value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} /></div>
                <div className="space-y-2"><Label>Antwort-E-Mail</Label><Input data-testid="company-reply" value={form.email_reply_to} onChange={(e) => setForm({ ...form, email_reply_to: e.target.value })} /></div>

                <div className="pt-3 mt-1 border-t border-slate-100">
                  <div className="text-sm font-semibold text-slate-900 mb-1">Admin-Zugang (optional)</div>
                  <p className="text-xs text-slate-400 mb-3">Legt einen Admin-Login an, den Sie dem Kunden zusenden können.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Name</Label><Input data-testid="company-admin-name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} placeholder="Max Mustermann" /></div>
                    <div className="space-y-2"><Label>Benutzername</Label><Input data-testid="company-admin-username" value={form.admin_username} onChange={(e) => setForm({ ...form, admin_username: e.target.value })} placeholder="klein, eindeutig" autoCapitalize="none" /></div>
                    <div className="space-y-2"><Label>Passwort</Label><Input data-testid="company-admin-password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder="mind. 4 Zeichen" /></div>
                    <div className="space-y-2"><Label>Telefon</Label><Input data-testid="company-admin-phone" value={form.admin_phone} onChange={(e) => setForm({ ...form, admin_phone: e.target.value })} placeholder="optional" /></div>
                    <div className="space-y-2 col-span-2"><Label>E-Mail</Label><Input data-testid="company-admin-email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="optional" /></div>
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="company-create" className="brand-bg text-white hover:opacity-90">Anlegen</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {rows.length === 0 ? (
        <Card className="border-slate-200 rounded-xl"><EmptyState icon={Building2} title="Keine Firmen" testid="firmen-empty" /></Card>
      ) : (
        <div className="grid gap-4">
          {rows.map((c) => {
            const suspended = c.status === "suspended";
            const pending = c.status === "pending";
            const hasOrders = (c.features || []).includes("orders");
            const hasMargin = (c.features || []).includes("margin");
            const manageEnabled = c.super_admin_manage_enabled !== false;
            return (
              <Card key={c.id} className="p-5 border-slate-200 rounded-xl" data-testid={`company-${c.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl brand-bg text-white flex items-center justify-center font-bold">{c.name?.charAt(0)}</div>
                    <div>
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {c.name}
                        <Badge variant="outline" className={pending ? "bg-amber-50 text-amber-700 border-amber-200" : suspended ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
                          {pending ? "Wartet auf Freischaltung" : suspended ? "Gesperrt" : "Aktiv"}
                        </Badge>
                        {c.self_signup && <Badge variant="outline" className="bg-slate-50 text-slate-500">Selbstregistrierung</Badge>}
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><Users className="h-3.5 w-3.5" /> {c.employee_count} Benutzer · <span className="font-mono text-xs">{c.id}</span></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {c.status === "active" && manageEnabled && (
                      <Button size="sm" onClick={() => manage(c)} data-testid={`manage-${c.id}`} className="brand-bg text-white hover:opacity-90"><Settings2 className="h-4 w-4 mr-1" /> Verwalten</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openAudit(c)} data-testid={`audit-${c.id}`} className="text-slate-700"><History className="h-4 w-4 mr-1" /> Protokoll</Button>
                    {pending ? (
                      <Button size="sm" onClick={() => setStatus(c.id, "active")} data-testid={`approve-${c.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-4 w-4 mr-1" /> Freischalten</Button>
                    ) : suspended ? (
                      <Button size="sm" variant="outline" onClick={() => setStatus(c.id, "active")} data-testid={`activate-${c.id}`} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4 mr-1" /> Aktivieren</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setStatus(c.id, "suspended")} data-testid={`suspend-${c.id}`} className="text-amber-600 border-amber-200 hover:bg-amber-50"><Ban className="h-4 w-4 mr-1" /> Sperren</Button>
                    )}
                    {c.super_admin_user_creation_enabled && c.status === "active" && (
                      <Button size="sm" variant="outline" onClick={() => setUserFor(c)} data-testid={`create-user-${c.id}`} className="text-slate-700"><UserPlus className="h-4 w-4 mr-1" /> Mitarbeiter</Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`delete-${c.id}`} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Firma löschen?</AlertDialogTitle><AlertDialogDescription>„{c.name}" wird endgültig entfernt. Diese Aktion kann nicht rückgängig gemacht werden.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={() => del(c.id)} className="bg-rose-600 hover:bg-rose-700">Löschen</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 pt-4 border-t border-slate-100">
                  <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <span className="text-sm font-medium text-slate-700">Aufträge/Baustellen</span>
                    <Switch checked={hasOrders} onCheckedChange={(v) => toggleModule(c.id, { orders: v })} data-testid={`module-orders-${c.id}`} />
                  </label>
                  <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <span className="text-sm font-medium text-slate-700">Margin Report</span>
                    <Switch checked={hasMargin} onCheckedChange={(v) => toggleModule(c.id, { margin: v })} data-testid={`module-margin-${c.id}`} />
                  </label>
                  <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <span className="text-sm font-medium text-slate-700">Fotos dauerhaft</span>
                    <Switch checked={!!c.photos_permanent} onCheckedChange={(v) => toggleModule(c.id, { photos_permanent: v })} data-testid={`module-photos-${c.id}`} />
                  </label>
                  <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <span className="text-sm font-medium text-slate-700">MA legt Aufträge an</span>
                    <Switch checked={!!c.employees_can_create_orders} onCheckedChange={(v) => toggleModule(c.id, { employees_can_create_orders: v })} data-testid={`module-emporders-${c.id}`} />
                  </label>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!userFor} onOpenChange={(v) => !v && setUserFor(null)}>
        <DialogContent data-testid="platform-user-dialog">
          <DialogHeader><DialogTitle>Mitarbeiter anlegen{userFor ? ` – ${userFor.name}` : ""}</DialogTitle><DialogDescription>Legt einen Mitarbeiter für diese Firma an (nur wenn die Firma es erlaubt).</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Name</Label><Input data-testid="platform-user-name" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Benutzername</Label><Input data-testid="platform-user-username" value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} placeholder="klein, eindeutig" /></div>
            </div>
            <div className="space-y-2"><Label>Passwort</Label><Input data-testid="platform-user-password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="mind. 4 Zeichen" /></div>
            <div className="space-y-2"><Label>Zugangsdaten per E-Mail senden (optional)</Label><Input data-testid="platform-user-notify" value={userForm.notify_email} onChange={(e) => setUserForm({ ...userForm, notify_email: e.target.value })} placeholder="kunde@firma.de" /></div>
            <div className="space-y-2">
              <Label>Anstellung</Label>
              <Select value={userForm.employee_type} onValueChange={(v) => setUserForm({ ...userForm, employee_type: v })}>
                <SelectTrigger data-testid="platform-user-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="festangestellt">Festangestellt</SelectItem><SelectItem value="haushilfe">Aushilfe</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={createUser} data-testid="platform-user-submit" className="brand-bg text-white hover:opacity-90">Anlegen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={(v) => !v && setCreated(null)}>
        <DialogContent data-testid="company-created-dialog">
          <DialogHeader><DialogTitle>Firma & Admin angelegt</DialogTitle><DialogDescription>Senden Sie diese Zugangsdaten an den Kunden.</DialogDescription></DialogHeader>
          {created && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm space-y-1.5 font-mono">
                <div><span className="text-slate-400">Firma:</span> {created.company}</div>
                <div><span className="text-slate-400">Portal:</span> {portalUrl}</div>
                <div><span className="text-slate-400">Benutzer:</span> {created.username}</div>
                <div><span className="text-slate-400">Passwort:</span> {created.password}</div>
              </div>
              <p className="text-xs text-slate-400">Das Passwort wird verschlüsselt gespeichert und ist danach nicht mehr abrufbar – jetzt kopieren oder per E-Mail senden.</p>
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <Label>Per E-Mail an den Kunden senden</Label>
                <div className="flex gap-2">
                  <Input data-testid="send-creds-email" value={created.email || ""} onChange={(e) => setCreated({ ...created, email: e.target.value })} placeholder="kunde@firma.de" />
                  <Button variant="outline" onClick={sendCreds} data-testid="send-creds-btn" className="shrink-0"><Mail className="h-4 w-4 mr-2" /> Senden</Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={copyCreds} data-testid="copy-creds"><Copy className="h-4 w-4 mr-2" /> Zugangsdaten kopieren</Button>
            <Button onClick={() => setCreated(null)} className="brand-bg text-white hover:opacity-90">Fertig</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!auditFor} onOpenChange={(v) => !v && setAuditFor(null)}>
        <DialogContent data-testid="audit-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Betreiber-Protokoll{auditFor ? ` – ${auditFor.name}` : ""}</DialogTitle><DialogDescription>Aktionen, die im Verwalten-Modus (Betreiber) durchgeführt wurden.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {auditRows.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center" data-testid="audit-empty">Keine protokollierten Aktionen.</div>
            ) : (
              auditRows.map((r) => (
                <div key={r.id} className="p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid={`audit-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 text-sm">{r.action}</div>
                      <div className="text-xs text-slate-400">Betreiber: {r.actor_username} · <span className="font-mono">{r.method} {r.path?.replace("/api", "")}</span></div>
                    </div>
                    <div className="text-xs text-slate-500 shrink-0">{r.at ? new Date(r.at).toLocaleString("de-DE") : ""}</div>
                  </div>
                  {Array.isArray(r.changes) && r.changes.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                      {r.changes.map((ch, i) => (
                        <div key={i} className="text-xs flex flex-wrap items-center gap-1.5" data-testid={`audit-change-${r.id}-${i}`}>
                          <span className="font-medium text-slate-600">{ch.label}:</span>
                          <span className="text-slate-400 line-through">{ch.old === null || ch.old === undefined || ch.old === "" ? "—" : String(ch.old)}</span>
                          <span className="text-slate-400">→</span>
                          <span className="font-medium text-slate-900">{ch.new === null || ch.new === undefined || ch.new === "" ? "—" : String(ch.new)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
